// Option vocabularies for the Company settings → Formats card.
//
// The `value` of every option is the WIRE token, confirmed against the backend
// — not a display string and not something to tidy. Two of them look like
// mistakes and are not:
//   · timeFormat is '12 hrs' / '24 hrs', with a space.
//   · dateFormat stores SLASHES ('dd/mm/yyyy') but displays DASHES
//     ('DD-MM-YYYY'). Do not "fix" either one to match the other.
//
// Labels resolve at render: from an i18n key when they are copy, from a
// literal when they are data (the date patterns). Everything funnels through
// `optionLabel`, so read mode and edit mode cannot drift.

import type { TFunction } from 'i18next';
import type { WeekDay } from '@/api/endpoints/companySettingsApi';
import type { AppLanguage } from '@/redux/language/languageSlice';

export interface SettingOption<V extends string = string> {
  value: V;
  /** i18n key — used when the label is copy. */
  labelKey?: string;
  /** Literal label — used when the label is data (a date pattern). */
  label?: string;
}

export const LANGUAGES: SettingOption<AppLanguage>[] = [
  { value: 'en', labelKey: 'profile_language_en' },
  { value: 'ar', labelKey: 'profile_language_ar' },
];

export const DATE_FORMATS: SettingOption[] = [
  { value: 'dd/mm/yyyy', label: 'DD-MM-YYYY' },
  { value: 'mm/dd/yyyy', label: 'MM-DD-YYYY' },
];

export const TIME_FORMATS: SettingOption[] = [
  { value: '12 hrs', labelKey: 'select_12hours' },
  { value: '24 hrs', labelKey: 'select_24hours' },
];

export const WEEK_DAYS: SettingOption<WeekDay>[] = [
  { value: 'sunday', labelKey: 'select_sunday' },
  { value: 'monday', labelKey: 'select_monday' },
  { value: 'tuesday', labelKey: 'select_tuesday' },
  { value: 'wednesday', labelKey: 'select_wednesday' },
  { value: 'thursday', labelKey: 'select_thursday' },
  { value: 'friday', labelKey: 'select_friday' },
  { value: 'saturday', labelKey: 'select_saturday' },
];

// Curated rather than Intl.supportedValuesOf('timeZone'), which needs
// lib: es2022 and yields 400+ rows nobody can scroll.
// TODO(backend): the timeZone vocabulary is the one Formats field still
// unconfirmed — these IANA ids are a placeholder, not a contract.
export const TIME_ZONES: SettingOption[] = [
  { value: 'Asia/Riyadh', label: '(GMT+03:00) Riyadh' },
  // { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai' },
  // { value: 'Asia/Kuwait', label: '(GMT+03:00) Kuwait' },
  // { value: 'Asia/Qatar', label: '(GMT+03:00) Doha' },
  // { value: 'Asia/Bahrain', label: '(GMT+03:00) Manama' },
  // { value: 'Asia/Muscat', label: '(GMT+04:00) Muscat' },
  // { value: 'Asia/Baghdad', label: '(GMT+03:00) Baghdad' },
  // { value: 'Asia/Amman', label: '(GMT+03:00) Amman' },
  // { value: 'Africa/Cairo', label: '(GMT+03:00) Cairo' },
  // { value: 'Asia/Karachi', label: '(GMT+05:00) Karachi' },
  // { value: 'Europe/London', label: '(GMT+01:00) London' },
  // { value: 'UTC', label: '(GMT+00:00) UTC' },
];

/** Support-hours day presets. The VALUE uses a plain hyphen, exactly as the
 *  backend stores it; the LABEL uses an en-dash, exactly as the design draws
 *  it. Do not tidy the value to match the label — that writes a character the
 *  contract never asked for. */
export const SUPPORT_DAYS: SettingOption[] = [
  { value: 'Sunday - Thursday', label: 'Sunday – Thursday' },
  { value: 'Monday - Friday', label: 'Monday – Friday' },
  { value: 'Saturday - Wednesday', label: 'Saturday – Wednesday' },
  { value: 'Monday - Saturday', label: 'Monday – Saturday' },
  { value: 'Every day', label: 'Every day' },
];

/** Half-hour slots across the day. Generated rather than hand-listed: 48
 *  literals would be 48 chances to typo one.
 *
 *  The VALUE is a 24-hour `HH:mm` time (`00:00` … `23:30`) — a real time
 *  format the backend can parse and compare; the LABEL is the 12-hour string
 *  the design shows (`12:00 AM` … `11:30 PM`). Always render a stored value
 *  through `labelOf`, never raw, or the UI prints `08:00` where it should say
 *  `8:00 AM`. */
export const TIME_SLOTS: SettingOption[] = Array.from({ length: 48 }, (_, i) => {
  const h24 = Math.floor(i / 2);
  const mm = i % 2 ? '30' : '00';
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return {
    value: `${String(h24).padStart(2, '0')}:${mm}`,
    label: `${hour12}:${mm} ${h24 < 12 ? 'AM' : 'PM'}`,
  };
});

/** Display string for one option. */
export const optionLabel = (option: SettingOption<string>, t: TFunction): string =>
  option.labelKey ? t(option.labelKey) : (option.label ?? option.value);

/** Display string for a stored value. Falls back to the raw value so a value
 *  outside the list still shows something rather than an empty row. */
export const labelOf = (options: SettingOption<string>[], value: string, t: TFunction): string => {
  const option = options.find((o) => o.value === value);
  return option ? optionLabel(option, t) : value;
};

/** Ready-made SelectDropdown options for a list. */
export const toSelectOptions = <V extends string>(
  options: SettingOption<V>[],
  t: TFunction,
): { value: V; label: string }[] =>
  options.map((o) => ({ value: o.value, label: optionLabel(o, t) }));
